import React, { useEffect, useRef } from "react";
import { Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { mapFittings } from "@/lib/pipe3dPoints";

function fitFont(g, text, maxW, px) {
  let size = px;
  do { g.font = `bold ${size}px sans-serif`; size -= 1; } while (g.measureText(text).width > maxW && size > 8);
}

function snapshot(invite) {
  const src = document.querySelector('[data-testid="pipeviz-3d-canvas"] canvas');
  if (!src) return null;
  const c = document.createElement("canvas");
  c.width = src.width; c.height = src.height;
  const g = c.getContext("2d");
  g.drawImage(src, 0, 0);
  const h = Math.round(c.height * 0.09);
  const pad = h * 0.4;
  g.fillStyle = "rgba(9,9,11,0.88)";
  g.fillRect(0, c.height - h, c.width, h);
  g.textBaseline = "middle";
  const host = window.location.host;
  fitFont(g, host, c.width * 0.3, Math.round(h * 0.34));
  const hostW = g.measureText(host).width;
  g.textAlign = "right"; g.fillStyle = "#e4e4e7";
  g.fillText(host, c.width - pad, c.height - h / 2);
  fitFont(g, invite, c.width - hostW - pad * 3, Math.round(h * 0.38));
  g.textAlign = "left"; g.fillStyle = "#f59e0b";
  g.fillText(invite, pad, c.height - h / 2);
  return new Promise((res) => c.toBlob(res, "image/png"));
}

export const ShareModel = ({ t, segments, diameter, fittings }) => {
  const link = useRef({ key: null, url: null });

  const getLink = async () => {
    const segs = segments.filter((s) => /^[+-][XYZ]$/.test(String(s.axis || "").trim().toUpperCase()))
      .map((s) => ({
        axis: String(s.axis).trim().toUpperCase(), length_mm: Number(s.length_mm) > 0 ? Number(s.length_mm) : null,
        offset_axis: /^[+-][XYZ]$/.test(String(s.offset_axis || "").trim().toUpperCase()) ? String(s.offset_axis).trim().toUpperCase() : null,
        offset_mm: Number(s.offset_mm) > 0 ? Number(s.offset_mm) : null,
      }));
    const fits = mapFittings(segments, fittings).map((f) => ({
      type: f.type, segment: f.seg + 1, position_mm: f.pos != null ? Math.round(f.pos * 1000) : null,
      ...(f.type === "tee" ? { branch_axis: f.branchAxis, branch_mm: f.branchLen ? Math.round(f.branchLen * 1000) : null } : {}),
    }));
    const key = JSON.stringify([segs, diameter, fits]);
    if (link.current.key !== key) {
      const r = await api.post("/pipe-viz/share", { segments: segs, pipe_diameter: diameter ? String(diameter).slice(0, 40) : null, fittings: fits });
      link.current = { key, url: `${window.location.origin}/izometria/${r.data.id}` };
    }
    return link.current.url;
  };

  useEffect(() => {
    const h = setTimeout(() => getLink().catch(() => {}), 800);
    return () => clearTimeout(h);
  }, [segments, diameter, fittings]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFile = async () => {
    const blob = await snapshot(t.invite);
    return blob && new File([blob], `tigik-izometria-${Date.now()}.png`, { type: "image/png" });
  };

  const download = async (file) => {
    const f = file || (await getFile());
    if (!f) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(f);
    a.download = f.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const share = async () => {
    let url;
    try { url = await getLink(); } catch { url = `${window.location.origin}/pipe-visualizer`; }
    const file = await getFile();
    if (!file) return;
    const text = `${t.shareText} ${url}\n\n${t.invite} 👉 ${window.location.origin}`;
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: t.tag, text }); } catch { /* cancelled */ }
      return;
    }
    await download(file);
    toast.success(t.shareFallback);
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const cls = "px-4 py-2.5 text-sm inline-flex items-center gap-2 transition-colors";
  return (
    <div className="flex flex-wrap gap-3" data-testid="pipeviz-share-bar">
      <button onClick={share} data-testid="pipeviz-share-btn" className={`wb-btn-primary ${cls}`}><Share2 className="w-4 h-4" /> {t.share}</button>
      <button onClick={() => download()} data-testid="pipeviz-download-btn" className={`${cls} border border-zinc-700 text-white hover:border-amber-500`}><Download className="w-4 h-4" /> {t.download}</button>
    </div>
  );
};
